import React from 'react';
import SummaryCard from './SummaryCard';

function FinancialGoals({ goals }) {
    // Helper for status display
    const getStatus = (g) => {
        if (g.status === 'goal_reached') {
            return (
                <span className="content-text font-medium text-teal-600 flex items-center gap-1">
                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Goal reached
                </span>
            );
        }
        if (g.status === 'not_started') {
            return <span className="content-text font-medium text-pink-500">Yet to start</span>;
        }
        if (g.status === 'in_progress') {
            return <span className="content-text font-medium text-gray-800">{g.completed}% Completed</span>;
        }
        return null;
    };

    // Helper for progress bar color
    const getBarColor = (g) => {
        if (g.status === 'goal_reached') return 'bg-teal-500';
        if (g.status === 'not_started') return 'bg-orange-400';
        return 'bg-gray-900';
    };

    // Helper for priority badge
    const getPriorityBadge = (g) => {
        // You can customize this based on the goal's priority data
        // For now, using a simple mapping, but this should come from your data
        const priority = g.priority || 'High'; // fallback to High if no priority specified

        if (priority === 'High') {
            return (
                <span className="bg-pink-100 text-pink-600 content-text text-xs font-medium rounded-full px-3 py-1">
                    High Priority
                </span>
            );
        }
        if (priority === 'Medium') {
            return (
                <span className="bg-orange-100 text-orange-600 content-text text-xs font-medium rounded-full px-3 py-1">
                    Medium Priority
                </span>
            );
        }
        if (priority === 'Low') {
            return (
                <span className="bg-gray-100 text-gray-600 content-text text-xs font-medium rounded-full px-3 py-1">
                    Low Priority
                </span>
            );
        }
        return null;
    };

    return (
        <SummaryCard title="Financial Goals">
            <div className="space-y-6">
                {goals?.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                        <p className="text-lg">🎯 No goals set up yet!</p>
                        <p className="text-sm mt-2">✨ Start your journey by defining your first goal</p>
                    </div>
                )}
                {goals?.length > 0 &&
                    (<div className="flex justify-between items-center mb-4">
                        <div></div> {/* Empty div for spacing */}
                        <span className="content-text text-gray-400 text-sm">Inflation adjusted</span>
                    </div>)}
                {goals?.map((g, idx) => (
                    <div key={g.name}>
                        {/* Goal Header */}
                        <div className="flex justify-between items-start mb-2">
                            <h4 className="content-text text-gray-900 font-medium text-lg">{g.name}</h4>
                            <div className="text-right">
                                {getStatus(g)}
                            </div>
                        </div>

                        {/* Target and Timeline */}
                        <div className="flex items-center gap-2 mb-3">
                            <span className="text-pink-500">🎯</span>
                            <span className="content-text text-gray-700">{g.targetText}</span>
                        </div>

                        {/* Progress Bar */}
                        <div className="w-full h-2 rounded-full bg-gray-200 mb-3">
                            <div
                                className={`${getBarColor(g)} h-full rounded-full transition-all duration-300`}
                                style={{ width: `${Math.min(100, Math.max(0, g.completed || 0))}%` }}
                            ></div>
                        </div>

                        {/* Priority Badge */}
                        <div className="flex justify-end">
                            {getPriorityBadge(g)}
                        </div>

                        {/* Separator for all but last item */}
                        {idx < goals.length - 1 && (
                            <div className="border-b border-gray-200 mt-6"></div>
                        )}
                    </div>
                ))}
            </div>
        </SummaryCard>
    );
}

export default FinancialGoals;
